import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { GoogleMap, Marker, InfoWindow, OverlayView, Polyline, MarkerClusterer } from '@react-google-maps/api'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { verifyToken } from '../../utils/helpers'
import { secureApi } from '../../Config/api'
import { GET } from '../../utils/apis'
import { mapBus } from '../../assets'
import { translations } from '../../utils/constants'
import io from 'socket.io-client'
import { throttle } from 'lodash'
import moment from 'moment'
import { useGoogleMaps } from '../../utils/googleMapsLoader'
import TraceEndpoints from '../TraceEndpoints/TraceEndpoints'

const containerStyle = {
  width: '100%',
  height: '100%',
}

const startColors = ['#FF0000', '#00AA00', '#0000FF', '#FF9900', '#9900FF', '#00CCCC', '#FF3399']

const DashboardMap = ({ language }) => {
  const { isLoaded: isMapScriptLoaded } = useGoogleMaps()
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector((state) => state?.authReducer?.user)
  const organizationDetails = useSelector((state) => state?.authReducer?.organization)
  const organizationId = organizationDetails?.id

  const [locations, setLocations] = useState([])
  const [directionsList, setDirectionsList] = useState([])
  const [startPoints, setStartPoints] = useState([])
  const [defaultLocation] = useState({ lat: 45.4642, lng: 9.1900 })
  const [isLoading, setIsLoading] = useState(true)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapZoom, setMapZoom] = useState(12)
  const [map, setMap] = useState(null)
  const [mapCenter, setMapCenter] = useState(null)
  const [startPoint, setStartPoint] = useState(null)
  // GTFS schedule for the clicked vehicle — official line + stops, same as
  // the main map.
  const [scheduleRoute, setScheduleRoute] = useState(null)
  const [isScheduleLoading, setIsScheduleLoading] = useState(false)
  const [selectedStopInfo, setSelectedStopInfo] = useState(null)
  const isFirstLoad = useRef(true)
  const userInteracted = useRef(false)

  useEffect(() => {
    verifyToken(history, dispatch)
  }, [])

  useEffect(() => {
    // Full fetch (positions + polylines) once; socket ticks refresh ONLY the
    // live positions — re-downloading every vehicle's polyline on each tick
    // is what made the maps hang as data grew (same fix as the main map).
    const throttledFetch = throttle(fetchPositionsOnly, 10000)
    fetchDeviceInfo()
    const socket = io()
    socket.on('connect', () => console.log('Socket connected'))
    socket.on('deviceinfo', throttledFetch)

    // Without a timer the buses only move when a socket event happens to
    // arrive — with no socket they froze entirely.
    const pollId = setInterval(fetchPositionsOnly, 15000)

    return () => {
      clearInterval(pollId)
      socket.off('deviceinfo', throttledFetch)
      socket.disconnect()
    }
  }, [])

  const fetchDeviceInfo = async () => {
    try {
      const user_id = user?.id
      // skip_polylines: positions only. Building every vehicle's road-snapped
      // trace on load meant one OSRM matching per bus before the dashboard
      // could paint; the selected bus's trace is fetched on demand below.
      const response = await secureApi.get(`${GET.GET_MAP_HISTORY}?user_id=${user_id}&organization_id=${organizationId}&skip_polylines=true`)

      if (response?.success && response?.data?.length) {
        const parsedLocations = response.data
          .map((device) => ({
            vehicleId: device?.vehicleId,
            lat: parseFloat(device.lat),
            lng: parseFloat(device.lng),
            vehicleName: device?.vehicleName,
            vehicleModel: device?.vehicleModel,
            logoUrl: device?.logoUrl,
            remaining: device?.remaining,
            is_online: device?.is_online,
            polyline: device?.polyline || [],
          }))
          .filter((loc) => !isNaN(loc.lat) && !isNaN(loc.lng))

        setLocations(parsedLocations)

        if (parsedLocations.length && isFirstLoad.current) {
          setMapCenter(parsedLocations[0])
          isFirstLoad.current = false
        }

        const allDirections = []
        const allStartDots = []

        for (let i = 0; i < parsedLocations.length; i++) {
          const location = parsedLocations[i]
          if (location.polyline?.length) {
            const data = location?.polyline
            allDirections?.push(data)
            setStartPoint(data[0])
            allStartDots.push({
              position: data[0],
              color: startColors[i % startColors.length],
            })
          }
          else {
            allDirections.push(null)
          }
        }

        setDirectionsList(allDirections)
        setStartPoints(allStartDots)
      }
      else {
        fallbackToGeolocation()
      }

      setIsLoading(false)
    }
    catch (error) {
      fallbackToGeolocation()
      setIsLoading(false)
    }
  }

  // Lightweight periodic refresh: move the markers, keep the loaded
  // polylines. Returns the same state reference when nothing moved so React
  // (and the clusterer) skip the re-render entirely.
  const fetchPositionsOnly = async () => {
    try {
      const user_id = user?.id
      const response = await secureApi.get(
        `${GET.GET_MAP_HISTORY}?user_id=${user_id}&organization_id=${organizationId}&positions_only=true`
      )
      if (!response?.success || !response?.data?.length) return

      const posMap = new Map(
        response.data
          .filter((p) => p.lat != null && p.lng != null && !Number.isNaN(parseFloat(p.lat)))
          .map((p) => [p.vehicleId, p])
      )

      setLocations((prev) => {
        if (!prev.length) return prev
        let changed = false
        const next = prev.map((v) => {
          const p = posMap.get(v.vehicleId)
          if (!p) return v
          const lat = parseFloat(p.lat)
          const lng = parseFloat(p.lng)
          if (v.lat === lat && v.lng === lng && v.is_online === p.is_online && v.remaining === p.remaining) {
            return v
          }
          changed = true
          return { ...v, lat, lng, is_online: p.is_online, remaining: p.remaining, report_time: p.report_time }
        })
        return changed ? next : prev
      })

      // The identity tooltip is a click-time snapshot — refresh it from the
      // same feed so its coordinates (and the position's report time) track
      // the live marker, like on the main map.
      setSelectedLocation((prev) => {
        if (!prev) return prev
        const p = posMap.get(prev.vehicleId)
        if (!p) return prev
        const lat = parseFloat(p.lat)
        const lng = parseFloat(p.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return prev
        if (
          prev.lat === lat &&
          prev.lng === lng &&
          prev.remaining === p.remaining &&
          prev.report_time === p.report_time
        ) {
          return prev
        }
        return { ...prev, lat, lng, remaining: p.remaining, is_online: p.is_online, report_time: p.report_time }
      })
    } catch (error) {
      // periodic refresh only — ignore transient failures
    }
  }

  // silent: refresh without clearing the currently drawn line (used by the
  // 15 s poll while a vehicle stays selected — delays/ETAs move).
  const fetchScheduleRoute = async (vehicleId, { silent = false } = {}) => {
    if (!vehicleId) {
      setScheduleRoute(null)
      return
    }
    if (!silent) {
      setScheduleRoute(null)
      setIsScheduleLoading(true)
    }
    try {
      const sr = await secureApi.get(`${GET?.GET_VEHICLE_SCHEDULE_ROUTE}?vehicle_id=${vehicleId}`)
      // A silent poll that fails or briefly loses the match keeps the line
      // that is already drawn instead of blanking it.
      // "Run finished" / "waiting at the start" are verdicts, not losses:
      // the route comes off the map at once.
      const definitive =
        !!sr?.success && !sr?.schedule_available && ['trip_completed', 'awaiting_departure'].includes(sr?.reason)
      setScheduleRoute((prev) =>
        silent && !definitive && prev?.schedule_available && !(sr?.success && sr?.schedule_available)
          ? prev
          : sr?.success ? sr : null
      )
      // Auto-fit the map to the whole route on the click itself — never on
      // silent refreshes, which must not yank the map mid-pan.
      if (!silent && sr?.success && sr?.shape?.length > 1 && map && window.google?.maps) {
        const bounds = new window.google.maps.LatLngBounds()
        sr.shape.forEach((p) => bounds.extend(p))
        map.fitBounds(bounds, 60)
      }
    } catch (err) {
      if (!silent) setScheduleRoute(null)
    } finally {
      if (!silent) setIsScheduleLoading(false)
    }
  }

  // The raw GPS trace (snapped=false) is a zig-zag of straight segments
  // between fixes — it used to be drawn at once and replaced by the road
  // line seconds later, which read as a broken map. Hold it: keep polling
  // for the road-snapped line and draw only that. The raw trace goes on the
  // map only as a last resort — snapping failed server-side (snap_failed),
  // or the polls ran out.
  const ROUTE_SNAP_ATTEMPTS = 12
  // Quick first re-polls (the road line usually lands within a second or
  // two of the first answer), then the slower steady cadence.
  const ROUTE_SNAP_POLL_MS = (attempt) => [1000, 1500, 2000][attempt] || 3000
  const routeFetchedRef = useRef(new Set())
  const fetchVehicleRoute = async (vehicleId, index, attempt = 0) => {
    try {
      const res = await secureApi.get(
        `${GET.GET_MAP_HISTORY}?user_id=${user?.id}&organization_id=${organizationId}&vehicle_id=${vehicleId}`
      )
      const route = res?.data?.[0]
      if (!res?.success || !route?.polyline?.length) return
      const stillSnapping = route.snapped === false && !route.snap_failed && attempt < ROUTE_SNAP_ATTEMPTS
      if (stillSnapping) {
        setTimeout(() => fetchVehicleRoute(vehicleId, index, attempt + 1), ROUTE_SNAP_POLL_MS(attempt))
        return
      }
      setDirectionsList((prev) => {
        const next = [...prev]
        next[index] = route.polyline
        return next
      })
    } catch (e) { }
  }

  useEffect(() => {
    if (selectedRouteIndex === null) return
    const vId = locations[selectedRouteIndex]?.vehicleId
    if (!vId) return
    if (!directionsList[selectedRouteIndex] && !routeFetchedRef.current.has(vId)) {
      routeFetchedRef.current.add(vId)
      fetchVehicleRoute(vId, selectedRouteIndex)
    }
    const id = setInterval(() => fetchScheduleRoute(vId, { silent: true }), 15000)
    return () => clearInterval(id)
  }, [selectedRouteIndex])

  const fallbackToGeolocation = () => {
    if ('geolocation' in navigator && isFirstLoad.current) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
          isFirstLoad.current = false
        },
        () => {
          setMapCenter(defaultLocation)
          isFirstLoad.current = false
        }
      )
    } else if (isFirstLoad.current) {
      setMapCenter(defaultLocation)
      isFirstLoad.current = false
    }
  }

  // const fetchDirections = useCallback((points) => {
  //   return new Promise((resolve, reject) => {
  //     if (!window.google || !window.google.maps || typeof window.google.maps.DirectionsService !== 'function') {
  //       return reject('Google Maps DirectionsService is not available yet.');
  //     }

  //     if (!points || points.length < 2) {
  //       return reject('Not enough points for directions.');
  //     }

  //     const validPoints = points.filter(
  //       (p, i, arr) =>
  //         p && typeof p.lat === 'number' && typeof p.lng === 'number' &&
  //         (i === 0 || Math.abs(p.lat - arr[i - 1].lat) > 0.0001 || Math.abs(p.lng - arr[i - 1].lng) > 0.0001)
  //     );

  //     if (validPoints.length < 2) {
  //       return reject('Not enough valid points after filtering.');
  //     }

  //     const origin = validPoints[0];
  //     const destination = validPoints[validPoints.length - 1];
  //     const allWaypoints = validPoints.slice(1, -1);

  //     const waypointChunks = [];
  //     while (allWaypoints.length > 0) {
  //       waypointChunks.push(allWaypoints.splice(0, 25));
  //     }

  //     const directionsService = new window.google.maps.DirectionsService();

  //     let resolved = false; // Only resolve once

  //     waypointChunks.forEach((waypoints, index) => {
  //       directionsService.route(
  //         {
  //           origin: new window.google.maps.LatLng(origin.lat, origin.lng),
  //           destination: new window.google.maps.LatLng(destination.lat, destination.lng),
  //           waypoints: waypoints.map((p) => ({
  //             location: new window.google.maps.LatLng(p.lat, p.lng),
  //             stopover: false,
  //           })),
  //           travelMode: window.google.maps.TravelMode.DRIVING,
  //           optimizeWaypoints: false,
  //         },
  //         (result, status) => {
  //           if (status === window.google.maps.DirectionsStatus.OK) {
  //             console.log(`Batch ${index + 1} directions fetched successfully`);

  //             if (!resolved && index === 0) {
  //               resolved = true;
  //               resolve(result);
  //             }
  //           }
  //           else {
  //             console.error(`Directions request failed for batch ${index + 1} due to`, status);
  //             if (!resolved && index === 0) {
  //               resolved = true;
  //               reject(`Directions request failed: ${status}`);
  //             }
  //           }
  //         }
  //       );
  //     });
  //   });
  // }, []);

  const fetchDirections = useCallback((points) => {
    return new Promise((resolve, reject) => {
      if (!points || points.length < 2) {
        return reject([]);
      }

      const validPoints = points.filter(
        (p, i, arr) =>
          p &&
          typeof p.lat === 'number' &&
          typeof p.lng === 'number' &&
          (i === 0 || Math.abs(p.lat - arr[i - 1].lat) > 0.0001 || Math.abs(p.lng - arr[i - 1].lng) > 0.0001)
      );

      if (validPoints.length < 2) {
        return reject('Not enough valid points after filtering.');
      }

      resolve(validPoints);
    });
  }, []);

  const getMarkerIcon = useCallback((logoUrl) => {
    if (!mapLoaded || !window.google || !window.google.maps) return null

    const baseSize = 35
    const scaleFactor = Math.pow(0.95, 12 - mapZoom)
    const size = Math.round(baseSize * scaleFactor)

    return {
      url: logoUrl || mapBus,
      scaledSize: new window.google.maps.Size(size, size),
    }
  }, [mapLoaded, mapZoom])

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance)
    setMapLoaded(true)

    const events = ['center_changed', 'zoom_changed', 'drag', 'dragend']
    events.forEach((event) => {
      mapInstance.addListener(event, () => {
        if (!userInteracted.current) {
          userInteracted.current = true
        }
      })
    })
  }, [])

  const onZoomChanged = useCallback(() => {
    if (map) {
      setMapZoom(map.getZoom())
    }
  }, [map])

  const getCurrentCenter = () => {
    if (userInteracted.current && map) {
      const center = map.getCenter()
      return { lat: center?.lat(), lng: center?.lng() }
    }
    return mapCenter || defaultLocation
  }

  // Where each vehicle's marker actually sits. Vehicles parked at the same
  // spot fully overlap — the clusterer showed a permanent "2" no zoom could
  // break apart. Spread co-located markers on a small circle (~15 m), same
  // as the main map.
  const adjustedPositions = useMemo(() => {
    const cellOf = (p) => `${p.lat?.toFixed?.(4)}|${p.lng?.toFixed?.(4)}` // ~11 m cell
    const cellMembers = {}
    locations.forEach((p, i) => {
      const k = cellOf(p)
      if (!cellMembers[k]) cellMembers[k] = []
      cellMembers[k].push(i)
    })

    return locations.map((p, i) => {
      const group = cellMembers[cellOf(p)] || []
      if (group.length < 2 || !Number.isFinite(p.lat)) return { lat: p.lat, lng: p.lng }
      const j = group.indexOf(i)
      const angle = (2 * Math.PI * j) / group.length
      const r = 0.00013 // ~15 m
      return {
        lat: p.lat + r * Math.cos(angle),
        lng: p.lng + (r * Math.sin(angle)) / Math.cos((p.lat * Math.PI) / 180),
      }
    })
  }, [locations])

  // Pulsing ring under the selected vehicle, following its live position.
  const highlightMemo = useMemo(() => {
    if (selectedRouteIndex === null) return null
    const p = adjustedPositions[selectedRouteIndex]
    if (!p || !Number.isFinite(p.lat)) return null
    return (
      <OverlayView position={p} mapPaneName={OverlayView.OVERLAY_LAYER}>
        <div className="vehicle-highlight-ring" />
      </OverlayView>
    )
  }, [selectedRouteIndex, adjustedPositions])

  const handleVehicleClick = (location, index) => {
    // Clicking the already-selected vehicle backs out of the focus: clear
    // the selection and bring the whole fleet back (same as the main map's
    // panel close / search clear).
    if (index === selectedRouteIndex) {
      setSelectedLocation(null)
      setSelectedRouteIndex(null)
      setSelectedStopInfo(null)
      setScheduleRoute(null)
      return
    }
    setSelectedLocation(location);
    setSelectedRouteIndex(index);
    setSelectedStopInfo(null);
    fetchScheduleRoute(location.vehicleId);
  }

  // The router platform's last_time arrives as 'YYYY-MM-DD HH:mm:ss' (or a
  // unix epoch on some firmware); render just the clock, like the platform's
  // own "Report time", so positions can be compared across dashboards.
  const formatReportTime = (t) => {
    if (!t) return null
    const m = /^\d{10}$/.test(String(t))
      ? moment.unix(Number(t))
      : moment(t, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true)
    return m.isValid() ? m.format('HH:mm:ss') : String(t)
  }

  const statusDot = (location, position) => {
    const baseSize = 40;
    const scaleFactor = Math.pow(0.95, 12 - mapZoom);
    const markerSize = Math.round(baseSize * scaleFactor);
    const dotOffsetBottom = markerSize * 1.1;
    const dotOffsetRight = markerSize * 0.45;
    const minScale = Math.max(0.4, scaleFactor);

    return (
      <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: location.is_online ? '#00ff00' : '#ff0000',
            borderRadius: '50%',
            border: '2px solid white',
            position: 'relative',
            bottom: `${dotOffsetBottom}px`,
            right: `-${dotOffsetRight}px`,
            transform: `scale(${minScale})`,
            zIndex: 1000,
          }}
        />
      </OverlayView>
    );
  }

  const markersMemo = useMemo(() => {
    const selectedLoc = selectedRouteIndex !== null ? locations[selectedRouteIndex] : null

    return (
      <>
        {/* Same rule as the main map: while a specific vehicle is selected,
            show ONLY it — no other markers or cluster badges around the
            focused route. Clearing the selection brings everyone back. */}
        {selectedRouteIndex === null && (
        <MarkerClusterer
          options={{
            styles: [
              {
                url: mapBus,
                height: 70,
                width: 60,
                textColor: "#ea050cff",
                textSize: 30,
                anchorText: [25, 35],
              }
            ],
            // Past street-level zoom every bus stands alone — without this,
            // co-located vehicles stayed clustered forever.
            maxZoom: 17,
          }}
          averageCenter
          enableRetinaIcons
          gridSize={60}
        >
          {(clusterer) =>
            locations?.map((location, index) => {
              // The selected vehicle renders OUTSIDE the clusterer (below) so
              // its icon — and the highlight ring under it — always sit on
              // the bus's true position, never on a cluster centroid.
              if (index === selectedRouteIndex) return null
              const markerPosition = adjustedPositions[index]

              return (
                <div key={index}>
                  <Marker
                    position={markerPosition}
                    onClick={() => handleVehicleClick(location, index)}
                    icon={getMarkerIcon(location.logoUrl)}
                    clusterer={clusterer}
                  />
                  {statusDot(location, markerPosition)}
                </div>
              );
            })
          }
        </MarkerClusterer>
        )}

        {selectedLoc && adjustedPositions[selectedRouteIndex] && (
          <>
            <Marker
              key={`selected-${selectedRouteIndex}`}
              position={adjustedPositions[selectedRouteIndex]}
              onClick={() => handleVehicleClick(selectedLoc, selectedRouteIndex)}
              icon={getMarkerIcon(selectedLoc.logoUrl)}
              zIndex={1001}
            />
            {statusDot(selectedLoc, adjustedPositions[selectedRouteIndex])}
          </>
        )}
      </>
    );
  }, [locations, adjustedPositions, getMarkerIcon, mapZoom, selectedRouteIndex]);

  // const polylinesMemo = useMemo(() => {
  //   return directionsList?.map((direction, index) => {
  //     if (!direction) return null;

  //     const isSelected = index === selectedRouteIndex;

  //     return (
  //       <Polyline
  //         key={`direction-${index}-${isSelected}`}
  //         path={direction}
  //         options={{
  //           strokeColor: isSelected ? '#FF0000' : '#4285F4',
  //           strokeWeight: 4,
  //           strokeOpacity: 1,
  //         }}
  //       />
  //     );
  //   });
  // }, [directionsList, selectedRouteIndex]);

  const polylinesMemo = useMemo(() => {
    if (selectedRouteIndex === null) return null;

    // Trip route (blue): the schedule's official road-following geometry.
    const tripLine =
      scheduleRoute?.shape?.length > 1 && window.google?.maps ? (
        <Polyline
          key="trip-shape"
          path={scheduleRoute.shape}
          options={{
            strokeColor: '#4285F4',
            strokeWeight: 4,
            strokeOpacity: 1,
            icons: [
              {
                icon: {
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 3,
                  strokeColor: '#4285F4',
                },
                offset: '0%',
                repeat: '450px',
              },
            ],
          }}
        />
      ) : null;

    // Vehicle route (red): the bus's actual GPS trace, drawn alongside the
    // trip route so the drift between schedule and reality stays visible.
    // S/E markers show where the trace begins and ends.
    const direction = directionsList[selectedRouteIndex];
    const busLine =
      !isScheduleLoading && direction ? (
        <Polyline
          key={`bus-trace-${selectedRouteIndex}`}
          path={direction}
          options={{
            strokeColor: '#FF0000',
            strokeWeight: 4,
            strokeOpacity: 1,
          }}
        />
      ) : null;

    const endpoints =
      !isScheduleLoading && direction ? (
        <TraceEndpoints key={`bus-endpoints-${selectedRouteIndex}`} path={direction} />
      ) : null;

    if (!tripLine && !busLine) return null;
    return (
      <>
        {tripLine}
        {busLine}
        {endpoints}
      </>
    );
  }, [directionsList, selectedRouteIndex, scheduleRoute, isScheduleLoading]);

  // The matched trip's stops — past/current/future dots, names at street
  // zoom, scheduled/estimated times on click. Same palette as the main map.
  const stopMarkersMemo = useMemo(() => {
    if (!mapLoaded || !window.google?.maps) return null
    if (selectedRouteIndex === null) return null

    return (scheduleRoute?.stops || [])
      .filter((s) => s?.lat != null && s?.lng != null)
      .map((s) => (
        <Marker
          key={`stop-${s.gtfs_stop_id}-${s.sequence}`}
          position={{ lat: parseFloat(s.lat), lng: parseFloat(s.lng) }}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: s.status === 'current' ? 7 : s.status === 'past' ? 4 : 5,
            fillColor:
              s.status === 'current' ? '#ff6d00' : s.status === 'past' ? '#90a4ae' : '#1a73e8',
            fillOpacity: s.status === 'past' ? 0.6 : 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            labelOrigin: new window.google.maps.Point(0, -2.2),
          }}
          label={
            mapZoom >= 16
              ? {
                  text: s.name?.length > 22 ? `${s.name.slice(0, 20)}…` : s.name,
                  color: '#0d47a1',
                  fontSize: '11px',
                  fontWeight: '700',
                }
              : undefined
          }
          zIndex={999}
          onClick={() =>
            setSelectedStopInfo({ ...s, lat: parseFloat(s.lat), lng: parseFloat(s.lng) })
          }
        />
      ))
  }, [scheduleRoute, selectedRouteIndex, mapLoaded, mapZoom]);

  // const startPointsMemo = useMemo(() => {
  //   return startPoints?.map((start, index) => {
  //     if (!start?.position) return null;

  //     const isSelected = index === selectedRouteIndex;

  //     return (
  //       <OverlayView
  //         key={`start-${index}`}
  //         position={start.position}
  //         mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
  //       >
  //         <div
  //           style={{
  //             width: '14px',
  //             height: '14px',
  //             backgroundColor: isSelected ? '#2175ff' : '#4285F4',
  //             borderRadius: '50%',
  //             border: '2px solid white',
  //             boxShadow: '0 0 6px rgba(0,0,0,0.5)',
  //             transform: 'translate(-50%, -50%)',
  //             zIndex: 999,
  //           }}
  //         />
  //       </OverlayView>
  //     );
  //   });
  // }, [startPoints, selectedRouteIndex]);

  return (
    <>
      {isMapScriptLoaded && (locations.length > 0 || !isLoading) ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={getCurrentCenter()}
          zoom={12}
          onLoad={onMapLoad}
          onZoomChanged={onZoomChanged}
          options={{
            gestureHandling: 'greedy',
            disableDefaultUI: true,
          }}
        >
          {markersMemo}
          {highlightMemo}
          {selectedLocation && (
            <InfoWindow
              position={
                (selectedRouteIndex !== null && adjustedPositions[selectedRouteIndex]) || {
                  lat: selectedLocation.lat,
                  lng: selectedLocation.lng,
                }
              }
              onCloseClick={() => {
                setSelectedLocation(null);
                setSelectedRouteIndex(null);
                setScheduleRoute(null);
                setSelectedStopInfo(null);
              }}
            >
              <div>
                <div><strong>{translations[language]?.vechicleName}:</strong> {selectedLocation.vehicleName}</div>
                <div><strong>{translations[language]?.vehicleModelLabel}:</strong> {selectedLocation.vehicleModel}</div>
                <div><strong>{translations[language]?.txtRemainingPassengers}:</strong> {selectedLocation.remaining || 0}</div>
                {Number.isFinite(selectedLocation.lat) && (
                  <div>
                    <strong>📍</strong> {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </div>
                )}
                {selectedLocation.report_time && (
                  <div>
                    <strong>🕒</strong> {formatReportTime(selectedLocation.report_time)}
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
          {selectedStopInfo && (
            <InfoWindow
              position={{ lat: selectedStopInfo.lat, lng: selectedStopInfo.lng }}
              onCloseClick={() => setSelectedStopInfo(null)}
            >
              <div>
                <div>
                  <strong>{selectedStopInfo.name}</strong>
                </div>
                {selectedStopInfo.scheduled && (
                  <div>
                    🕒 {translations[language]?.scheduledShort}:{' '}
                    <strong>{selectedStopInfo.scheduled}</strong>
                    {selectedStopInfo.estimated &&
                      selectedStopInfo.estimated !== selectedStopInfo.scheduled && (
                        <>
                          {' · '}
                          {translations[language]?.estimatedShort}:{' '}
                          <strong>{selectedStopInfo.estimated}</strong>
                        </>
                      )}
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
          {/* {startPointsMemo} */}
          {polylinesMemo}
          {stopMarkersMemo}
        </GoogleMap>
      ) : (
        <p>{translations[language]?.loadingMap}</p>
      )}
    </>
  )
}

export default DashboardMap
